import { useState } from "react";

import { useAPI, useRoleContext, useSwrFetch } from "hooks";
import {
  filterAndSortTemplates,
  shipmentTemplatesListUrl,
} from "utils/templates";

import { ShipmentTemplateMenu } from "./ShipmentTemplateMenu";

import type { ShipmentTemplate } from "types/shipment";

interface Props {
  onTemplateClick: (template: ShipmentTemplate) => void;
  /**
   * Held in the loading state until the caller can resolve a template's
   * carriers against the poster's network. Applying one before then would
   * seed the form with recipients that are never recalculated.
   */
  waitingOnCallerData?: boolean;
}

export function ShipmentTemplateMenuContainer(props: Props) {
  const { onTemplateClick, waitingOnCallerData = false } = props;
  const { role } = useRoleContext();
  const { api } = useAPI();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [deletedTemplate, setDeletedTemplate] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const templatesUrl =
    role?.company && role?.id ? shipmentTemplatesListUrl(role.id) : null;

  const {
    data: templates,
    loading,
    error: fetchError,
    refetch,
  } = useSwrFetch<ShipmentTemplate[]>({
    url: templatesUrl,
    errorMessage: "Failed to fetch templates. Please contact us.",
  });

  const onTemplateClicked = (template: ShipmentTemplate) => {
    onTemplateClick(template);
    setSelectedTemplateId(template.id);
  };

  const onDeleteTemplate = (template: ShipmentTemplate) => {
    setDeleteError(null);
    api<unknown>("delete", `/shipment-templates/${template.id}`)
      .then(() => {
        setDeletedTemplate(true);
        refetch();
      })
      .catch((e) => {
        setDeleteError(
          e?.response?.data?.message ?? "Failed to delete shipment template",
        );
      });
  };

  const filteredAndSortedTemplates = filterAndSortTemplates(
    templates ?? [],
    searchQuery,
  );

  return (
    <ShipmentTemplateMenu
      error={fetchError ?? deleteError}
      loading={loading || waitingOnCallerData}
      templates={filteredAndSortedTemplates}
      onTemplateClicked={onTemplateClicked}
      selectedTemplateId={selectedTemplateId}
      onDeleteTemplate={onDeleteTemplate}
      deletedTemplate={deletedTemplate}
      onCloseDeletedTemplate={() => {
        setDeletedTemplate(false);
      }}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
    />
  );
}
